import { useState, useMemo } from 'react';
import { AlertTriangle, X, Calendar } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Zeigt eine Warnung an, wenn übertragener Resturlaub aus dem Vorjahr
 * zum 31. März zu verfallen droht.
 *
 * Wird nur angezeigt zwischen 1. Januar und 31. März, wenn ein
 * nicht-verfallener Übertrag existiert.
 */
export function CarryOverWarning() {
  const { state, getCarryOverFromPreviousYear } = useApp();
  const [dismissed, setDismissed] = useState(false);

  const warningInfo = useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-11
    const currentYear = today.getFullYear();

    // Nur Januar (0), Februar (1), März (2) anzeigen
    if (currentMonth > 2) {
      return null;
    }

    // Prüfe ob wir im richtigen Jahr sind
    if (state.year !== currentYear) {
      return null;
    }

    const carryOverDays = getCarryOverFromPreviousYear();
    if (carryOverDays <= 0) {
      return null;
    }

    // Berechne verbleibende Tage bis zum Verfall (31. März)
    const expirationDate = new Date(currentYear, 2, 31); // März = 2
    const diffTime = expirationDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Dringlichkeitsstufe bestimmen
    let urgency: 'low' | 'medium' | 'high';
    if (daysRemaining <= 7) {
      urgency = 'high';
    } else if (daysRemaining <= 21) {
      urgency = 'medium';
    } else {
      urgency = 'low';
    }

    return {
      days: carryOverDays,
      daysRemaining,
      urgency,
      fromYear: state.carryOverVacation?.fromYear || currentYear - 1,
    };
  }, [state.year, state.carryOverVacation, getCarryOverFromPreviousYear]);

  // Nicht anzeigen wenn keine Warnung oder bereits geschlossen
  if (!warningInfo || dismissed) {
    return null;
  }

  const { days, daysRemaining, urgency, fromYear } = warningInfo;

  // Farben basierend auf Dringlichkeit — Alert-Card-Pattern
  const colors = {
    high: {
      container: 'border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10',
      text: 'text-red-800 dark:text-red-200',
      icon: 'text-red-600 dark:text-red-400',
      button: 'text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100',
    },
    medium: {
      container: 'border-amber-300/60 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/10',
      text: 'text-amber-800 dark:text-amber-200',
      icon: 'text-amber-600 dark:text-amber-400',
      button: 'text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100',
    },
    low: {
      container: 'border-amber-300/60 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/10',
      text: 'text-amber-800 dark:text-amber-200',
      icon: 'text-amber-600 dark:text-amber-400',
      button: 'text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100',
    },
  };

  const c = colors[urgency];

  const getMessage = () => {
    if (daysRemaining <= 0) {
      return `Achtung: Ihr übertragener Resturlaub aus ${fromYear} verfällt heute!`;
    }
    if (daysRemaining === 1) {
      return `Achtung: Ihr übertragener Resturlaub aus ${fromYear} verfällt morgen!`;
    }
    if (daysRemaining <= 7) {
      return `Dringend: Ihr übertragener Resturlaub aus ${fromYear} verfällt in ${daysRemaining} Tagen!`;
    }
    if (daysRemaining <= 14) {
      return `Erinnerung: Ihr übertragener Resturlaub aus ${fromYear} verfällt in ${daysRemaining} Tagen.`;
    }
    return `Hinweis: Sie haben noch ${days} Tage Resturlaub aus ${fromYear}, der am 31. März verfällt.`;
  };

  return (
    <div className={cn(c.container, 'border rounded-lg')}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={cn('flex-shrink-0', c.icon)}>
              {urgency === 'high' ? (
                <AlertTriangle className="w-5 h-5" />
              ) : (
                <Calendar className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', c.text)}>
                {getMessage()}
              </p>
              <p className={cn('text-xs opacity-80 mt-0.5', c.text)}>
                {days} {days === 1 ? 'Tag' : 'Tage'} Urlaubsübertrag
                {daysRemaining > 0 ? ` - noch ${daysRemaining} Tage bis zum Verfall (31.03.)` : ''}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setDismissed(true)}
            variant="ghost"
            size="icon"
            className={cn('flex-shrink-0 h-7 w-7 rounded-full hover:bg-white/50', c.button)}
            aria-label="Warnung schließen"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
